<template>
    <v-row class="picker-row" align="center">
        <v-col cols="auto" class="quick-buttons">
            <v-btn data-id="PrevMonth" variant="text" tile flat :class="{ activeBtn: isPreviousMonth }" @click="setPreviousMonth">
                {{ t('mpcStatistics.resources.previousMonth') }}
            </v-btn>
            <v-btn data-id="CurrentMonth" variant="text" tile flat :class="{ activeBtn: isCurrentMonth }" @click="setCurrentMonth">
                {{ t('mpcStatistics.resources.currentMonth') }}
            </v-btn>
        </v-col>
        <v-col cols="auto">
            <v-select
                v-model="from"
                data-id="FromYearMonth"
                :label="t('mpcStatistics.resources.from')"
                color="primary"
                variant="outlined"
                hide-details
                :items="monthOptions"
                style="min-width: 140px"
            />
        </v-col>
        <v-col cols="auto">
            <v-select
                v-model="to"
                data-id="ToYearMonth"
                :label="t('mpcStatistics.resources.to')"
                color="primary"
                variant="outlined"
                hide-details
                :items="monthOptions"
                style="min-width: 140px"
            />
        </v-col>
        <v-col v-if="$slots.actions" cols="auto" class="actions-col">
            <slot name="actions" />
        </v-col>
        <v-col v-if="error" cols="12">
            <v-alert type="warning" variant="flat">{{ error }}</v-alert>
        </v-col>
    </v-row>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatYearMonth } from '../monthFormat';

const { t } = useI18n();

interface Props {
    modelValue: { from: string; to: string };
}
const props = defineProps<Props>();
const emit = defineEmits<{
    (e: 'update:modelValue', value: { from: string; to: string }): void;
    (e: 'valid', valid: boolean): void;
    (e: 'reload'): void;
}>();

const from = ref(props.modelValue.from);
const to = ref(props.modelValue.to);

const monthValues = computed(() => {
    const now = new Date();
    const out: string[] = [];
    for (let i = 0; i < 60; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    }
    return out;
});

const monthOptions = computed(() => monthValues.value.map(value => ({ value, title: formatYearMonth(value) })));

const isCurrentMonth = computed(() => from.value === monthValues.value[0] && to.value === monthValues.value[0]);
const isPreviousMonth = computed(() => from.value === monthValues.value[1] && to.value === monthValues.value[1]);

const error = computed(() => {
    if (to.value < from.value) return t('mpcStatistics.resources.rangeOrderError');
    if (monthsBetween(from.value, to.value) > 11) return t('mpcStatistics.resources.maxPeriodError');
    return '';
});

watch([from, to], () => {
    emit('valid', error.value === '');
    if (error.value === '') emit('update:modelValue', { from: from.value, to: to.value });
});

watch(
    () => props.modelValue,
    val => {
        from.value = val.from;
        to.value = val.to;
    }
);

function setCurrentMonth() {
    applyMonth(monthValues.value[0]);
}

function setPreviousMonth() {
    applyMonth(monthValues.value[1] ?? monthValues.value[0]);
}

function applyMonth(ym: string) {
    from.value = ym;
    to.value = ym;
    emit('update:modelValue', { from: ym, to: ym });
    emit('reload');
}

function monthsBetween(a: string, b: string): number {
    const [ay, am] = a.split('-').map(Number);
    const [by, bm] = b.split('-').map(Number);
    return (by - ay) * 12 + (bm - am);
}

onMounted(() => {
    emit('valid', error.value === '');
});
</script>

<style scoped>
.picker-row {
    padding-top: 12px;
}

.quick-buttons {
    margin-right: 48px;
}

.actions-col {
    margin-left: -12px;
}

.activeBtn {
    border-bottom: 2px solid #258752;
}
</style>
